// src/pages/Public/Home.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import logo from "../../assets/images/cac-im.jpeg";
import {
  AirIcon,
  SeaIcon,
  StoreIcon,
  TrackingIcon,
} from "../../components/Icons/ServiceIcons";

// Composant pour les drapeaux avec fallback optimisé
const FlagImage = ({ countryCode, countryName }) => {
  const [imgError, setImgError] = useState(false);

  // URLs de fallback multiples
  const flagUrls = [
    `https://flagcdn.com/w80/${countryCode}.png`,
    `https://flagpedia.net/data/flags/icon/72x54/${countryCode}.png`,
    `https://cdn.jsdelivr.net/npm/country-flag-icons/3x2/${countryCode.toUpperCase()}.svg`,
  ];

  const currentSrc = imgError ? flagUrls[1] : flagUrls[0];

  return (
    <img
      src={currentSrc}
      alt={`Drapeau ${countryName}`}
      className="flag-image"
      loading="lazy"
      width="60"
      height="40"
      onError={() => setImgError(true)}
    />
  );
};

// Composant Skeleton pour le chargement
const SkeletonLoader = () => (
  <div className="skeleton-loader">
    <div className="skeleton-hero"></div>
    <div className="skeleton-section">
      <div className="skeleton-title"></div>
      <div className="skeleton-cards">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="skeleton-card"></div>
        ))}
      </div>
    </div>
  </div>
);

const Home = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [animated, setAnimated] = useState(false);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    // Simuler un court chargement pour les animations
    const timer = setTimeout(() => {
      setAnimated(true);
      setPageLoading(false);
    }, 500);

    fetchWhatsappNumbers();

    return () => clearTimeout(timer);
  }, []);

  const fetchWhatsappNumbers = async () => {
    try {
      // Utilisation de l'URL relative (configurée dans axios)
      const response = await axios.get("/whatsapp/public/");
      let numbers = [];
      if (response.data && response.data.results) {
        numbers = response.data.results;
      } else if (Array.isArray(response.data)) {
        numbers = response.data;
      }
      if (numbers.length === 0) {
        setWhatsappNumbers([
          { phone_number: "+237 612345678", is_default: true },
        ]);
      } else {
        setWhatsappNumbers(numbers);
      }
    } catch (err) {
      console.error("Erreur chargement numéros WhatsApp:", err);
      setWhatsappNumbers([
        { phone_number: "+237 612345678", is_default: true },
      ]);
    } finally {
      setLoadingWhatsapp(false);
    }
  };

  const cleanPhoneNumber = (phone) => {
    if (!phone) return "";
    return String(phone)
      .replace(/\s+/g, "")
      .replace(/[^0-9+]/g, "");
  };

  const defaultNumber =
    whatsappNumbers.find((n) => n.is_default) || whatsappNumbers[0];
  const mainWhatsapp = defaultNumber?.phone_number || "+237 612345678";

  const destinations = [
    { name: "GABON", flagCode: "ga", color: "#2E7D32" },
    { name: "MALI", flagCode: "ml", color: "#F9A825" },
    { name: "CONGO", flagCode: "cg", color: "#1565C0" },
    { name: "CÔTE D'IVOIRE", flagCode: "ci", color: "#F57F17" },
    { name: "TANZANIE", flagCode: "tz", color: "#4CAF50" },
  ];

  const services = [
    {
      title: "Transport Aérien",
      desc: "Livraison express en 5-10 jours",
      icon: <AirIcon />,
      color: "#1565C0",
      delay: 0,
    },
    {
      title: "Transport Maritime",
      desc: "Solutions économiques en 45-90 jours",
      icon: <SeaIcon />,
      color: "#2E7D32",
      delay: 100,
    },
    {
      title: "CAC Store",
      desc: "Achetez en ligne, livré chez vous",
      icon: <StoreIcon />,
      color: "#F9A825",
      delay: 200,
    },
    {
      title: "Tracking",
      desc: "Suivez vos colis en temps réel",
      icon: <TrackingIcon />,
      color: "#C62828",
      delay: 300,
    },
  ];

  const stats = [
    { value: "10+", label: "Années d'expérience", icon: "🏆" },
    { value: "20000+", label: "Colis transportés", icon: "📦" },
    { value: "5", label: "Pays desservis", icon: "🌍" },
    { value: "24/7", label: "Support client", icon: "💬" },
  ];

  const handleTracking = (e) => {
    e.preventDefault();
    const trackingNumberTrimmed = trackingNumber?.trim();

    if (trackingNumberTrimmed) {
      window.location.href = `/tracking/${trackingNumberTrimmed}`;
    }
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  // Afficher le skeleton pendant le chargement
  if (pageLoading) {
    return (
      <>
        <Helmet>
          <title>CAC - Chine Afrique Cargo | Transport International</title>
          <meta
            name="description"
            content="Solutions logistiques entre la Chine et l'Afrique. Transport aérien, maritime, tracking en temps réel et accompagnement personnalisé."
          />
          <meta
            name="keywords"
            content="transport chine afrique, cargo chine gabon, fret maritime chine mali, livraison express chine côte d'ivoire"
          />
          <meta name="author" content="CAC - Chine Afrique Cargo" />
          <meta name="robots" content="index, follow" />
          <meta property="og:title" content="CAC - Chine Afrique Cargo" />
          <meta
            property="og:description"
            content="Solution logistique fiable entre la Chine et l'Afrique"
          />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <link rel="canonical" href={window.location.origin} />
        </Helmet>
        <SkeletonLoader />
      </>
    );
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>CAC - Chine Afrique Cargo | Transport Chine-Afrique</title>
        <meta
          name="description"
          content="CAC - Solution logistique fiable et rapide pour vos marchandises entre la Chine et l'Afrique. Transport aérien, maritime, tracking en temps réel et accompagnement personnalisé."
        />
        <meta
          name="keywords"
          content="transport chine afrique, cargo chine gabon, fret maritime, livraison chine mali, transport aérien chine congo, chineafrique cargo, logisticien chine afrique"
        />
        <meta name="author" content="CAC - Chine Afrique Cargo" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="French" />
        <meta name="revisit-after" content="7 days" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin} />
        <meta
          property="og:title"
          content="CAC - Chine Afrique Cargo | Transport International"
        />
        <meta
          property="og:description"
          content="Solution logistique fiable entre la Chine et l'Afrique. Transport aérien, maritime, tracking en temps réel."
        />
        <meta
          property="og:image"
          content="https://chineafriquecargo.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CAC - Chine Afrique Cargo" />
        <meta
          name="twitter:description"
          content="Solution logistique fiable entre la Chine et l'Afrique"
        />

        {/* Canonical URL */}
        <link rel="canonical" href={window.location.origin} />

        {/* Structured Data / JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "CAC - Chine Afrique Cargo",
            url: window.location.origin,
            logo: "https://chineafriquecargo.com/logo.png",
            description: "Solution logistique entre la Chine et l'Afrique",
            address: {
              "@type": "PostalAddress",
              addressCountry: "China",
            },
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer service",
              telephone: mainWhatsapp,
            },
            sameAs: [
              "https://wa.me/" + cleanPhoneNumber(mainWhatsapp),
              "https://www.facebook.com/chineafriquecargo",
              "https://www.linkedin.com/company/chineafriquecargo",
            ],
          })}
        </script>
      </Helmet>

      {/* Hero Section Moderne */}
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <Container className="hero-container">
          <Row className="min-vh-100 align-items-center">
            <Col
              lg={7}
              className={`hero-content ${animated ? "fade-in-up" : ""}`}
            >
              <h1 className="hero-title">
                CHINE <span className="text-gradient">AFRIQUE CARGO</span>
              </h1>
              <p className="hero-subtitle">
                Solution logistique fiable et rapide pour vos marchandises.
                Connectez la Chine à l'Afrique avec CAC.
              </p>
              <div className="hero-buttons">
                <Button as={Link} to="/contact" className="btn-primary-custom">
                  📦 Demander un devis
                </Button>
                <Button as={Link} to="/tracking" className="btn-outline-custom">
                  📍 Suivre mon colis
                </Button>
              </div>
              <Form onSubmit={handleTracking} className="hero-tracking">
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Entrez votre numéro de suivi (ex: CAC12345678)"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    className="tracking-input"
                  />
                  <Button type="submit" className="tracking-btn">
                    Suivre
                  </Button>
                </InputGroup>
              </Form>
            </Col>
            <Col lg={5} className="d-none d-lg-block">
              <div className="hero-stats">
                {stats.map((stat, idx) => (
                  <div key={idx} className="stat-card">
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Destinations Section */}
      <Container className="destinations-section">
        <div className="section-header">
          <span className="section-badge">DESTINATIONS</span>
          <h2 className="section-title">
            Nos présences en <span className="text-gradient">Afrique</span>
          </h2>
          <p className="section-subtitle">
            Des bureaux locaux pour vous accompagner
          </p>
        </div>
        <Row className="g-4 justify-content-center">
          {destinations.map((dest, index) => (
            <Col key={index} md={6} lg={2.4} className="text-center">
              <div
                className="destination-card"
                style={{ borderBottomColor: dest.color }}
              >
                <div className="destination-flag">
                  <FlagImage
                    countryCode={dest.flagCode}
                    countryName={dest.name}
                  />
                </div>
                <div className="destination-name">{dest.name}</div>
                <div
                  className="destination-line"
                  style={{ backgroundColor: dest.color }}
                ></div>
              </div>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Services Section avec Icônes SVG */}
      <div className="services-section">
        <Container>
          <div className="section-header">
            <span className="section-badge">NOS SERVICES</span>
            <h2 className="section-title">
              Des solutions <span className="text-gradient">sur mesure</span>
            </h2>
            <p className="section-subtitle">
              Une gamme complète de services logistiques
            </p>
          </div>
          <Row className="g-4">
            {services.map((service, idx) => (
              <Col key={idx} md={6} lg={3}>
                <Card
                  className="service-card"
                  style={{ animationDelay: `${service.delay}ms` }}
                >
                  <div
                    className="service-icon"
                    style={{
                      backgroundColor: `${service.color}15`,
                      color: service.color,
                    }}
                  >
                    {service.icon}
                  </div>
                  <Card.Body>
                    <Card.Title className="service-title">
                      {service.title}
                    </Card.Title>
                    <Card.Text className="service-desc">
                      {service.desc}
                    </Card.Text>
                    <div
                      className="service-hover-line"
                      style={{ backgroundColor: service.color }}
                    ></div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* About Section avec Logo optimisé */}
      <div className="about-section">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <div className="about-image">
                <div className="about-image-badge">
                  <span>10+ ans</span>
                </div>
                <div className="about-image-content">
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="CAC - Chine Afrique Cargo Logo"
                      className="about-logo"
                      onError={handleLogoError}
                      loading="lazy"
                      width="300"
                      height="200"
                      srcSet={`${logo}?w=300 300w, ${logo}?w=600 600w`}
                      sizes="(max-width: 768px) 300px, 600px"
                    />
                  ) : (
                    <div className="about-icon-fallback">🚚</div>
                  )}
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="section-header text-start">
                <span className="section-badge">À PROPOS</span>
                <h2 className="section-title">
                  Votre partenaire{" "}
                  <span className="text-gradient">logistique de confiance</span>
                </h2>
                <p className="section-subtitle">
                  Depuis plus de 10 ans, CAC connecte les commerçants chinois
                  aux marchés africains.
                </p>
              </div>
              <div className="about-features">
                <div className="about-feature">
                  <div className="feature-icon">✅</div>
                  <div className="feature-text">
                    <strong>Fiabilité</strong>
                    <p>
                      Votre marchandise arrive à destination en toute sécurité
                    </p>
                  </div>
                </div>
                <div className="about-feature">
                  <div className="feature-icon">📊</div>
                  <div className="feature-text">
                    <strong>Transparence</strong>
                    <p>Suivi en temps réel et communication claire</p>
                  </div>
                </div>
                <div className="about-feature">
                  <div className="feature-icon">⚡</div>
                  <div className="feature-text">
                    <strong>Réactivité</strong>
                    <p>Équipe disponible 7j/7 pour répondre à vos besoins</p>
                  </div>
                </div>
                <div className="about-feature">
                  <div className="feature-icon">🌍</div>
                  <div className="feature-text">
                    <strong>Proximité</strong>
                    <p>Des bureaux en Chine et dans 5 pays africains</p>
                  </div>
                </div>
              </div>
              <Button
                as={Link}
                to="/about"
                variant="outline-danger"
                className="mt-4 px-4 py-2 rounded-pill"
              >
                En savoir plus →
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <Container className="text-center">
          <h2 className="cta-title">Prêt à expédier ?</h2>
          <p className="cta-subtitle">
            Contactez-nous dès maintenant pour un devis gratuit et personnalisé
          </p>
          <div className="cta-buttons">
            <Button as={Link} to="/contact" className="btn-cta-primary">
              📞 Nous contacter
            </Button>
            <Button
              as="a"
              href={`https://wa.me/${cleanPhoneNumber(mainWhatsapp)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cta-whatsapp"
            >
              💬 WhatsApp
            </Button>
          </div>
          {whatsappNumbers.length > 1 && !loadingWhatsapp && (
            <div className="other-numbers mt-3">
              <small className="text-white-50">Autres numéros :</small>
              <div className="d-flex justify-content-center gap-2 flex-wrap mt-1">
                {whatsappNumbers
                  .filter((n) => !n.is_default)
                  .map((num, idx) => (
                    <a
                      key={idx}
                      href={`https://wa.me/${cleanPhoneNumber(num.phone_number)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white-50 text-decoration-none small mx-1"
                    >
                      📱 {num.phone_number}
                    </a>
                  ))}
              </div>
            </div>
          )}
        </Container>
      </div>

      <style>{`
                /* Hero Section */
                .hero-section {
                    position: relative;
                    background: linear-gradient(135deg, #0a0a2a 0%, #1a1a4e 50%, #0a0a2a 100%);
                    overflow: hidden;
                }
                
                .hero-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: radial-gradient(circle at 20% 50%, rgba(198, 40, 40, 0.15) 0%, transparent 50%);
                }
                
                .hero-container {
                    position: relative;
                    z-index: 2;
                }
                
                .hero-badge {
                    margin-bottom: 24px;
                }
                
                .badge-pulse {
                    display: inline-block;
                    padding: 8px 20px;
                    background: rgba(249, 168, 37, 0.15);
                    border: 1px solid rgba(249, 168, 37, 0.3);
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #F9A825;
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(249, 168, 37, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0); }
                }
                
                .hero-title {
                    font-size: 56px;
                    font-weight: 800;
                    line-height: 1.2;
                    color: white;
                    margin-bottom: 20px;
                }
                
                .text-gradient {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                
                .hero-subtitle {
                    font-size: 18px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 32px;
                    line-height: 1.6;
                }
                
                .hero-buttons {
                    display: flex;
                    gap: 16px;
                    margin-bottom: 32px;
                    flex-wrap: wrap;
                }
                
                .btn-primary-custom {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    padding: 12px 28px;
                    border-radius: 50px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    color: white;
                }
                
                .btn-primary-custom:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(198, 40, 40, 0.4);
                }
                
                .btn-outline-custom {
                    background: transparent;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    padding: 12px 28px;
                    border-radius: 50px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    color: white;
                }
                
                .btn-outline-custom:hover {
                    background: rgba(255, 255, 255, 0.1);
                    border-color: white;
                    transform: translateY(-2px);
                }
                
                .hero-tracking {
                    max-width: 450px;
                }
                
                .tracking-input {
                    border-radius: 50px 0 0 50px;
                    border: none;
                    padding: 14px 20px;
                }
                
                .tracking-btn {
                    border-radius: 0 50px 50px 0;
                    background: #F9A825;
                    border: none;
                    color: #212121;
                    font-weight: 600;
                    padding: 14px 24px;
                }
                
                .tracking-btn:hover {
                    background: #F57F17;
                }
                
                .hero-stats {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 20px;
                    background: rgba(255, 255, 255, 0.05);
                    backdrop-filter: blur(10px);
                    border-radius: 24px;
                    padding: 30px;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                
                .stat-card {
                    text-align: center;
                    padding: 20px;
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 16px;
                    transition: all 0.3s ease;
                }
                
                .stat-card:hover {
                    transform: translateY(-5px);
                    background: rgba(255, 255, 255, 0.1);
                }
                
                .stat-icon {
                    font-size: 32px;
                    margin-bottom: 12px;
                }
                
                .stat-value {
                    font-size: 32px;
                    font-weight: 800;
                    color: #F9A825;
                }
                
                .stat-label {
                    font-size: 14px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-top: 8px;
                }
                
                /* Destinations Section */
                .destinations-section {
                    padding: 80px 0;
                    background: #f8f9fa;
                }
                
                .section-header {
                    text-align: center;
                    margin-bottom: 50px;
                }
                
                .section-badge {
                    display: inline-block;
                    padding: 6px 16px;
                    background: rgba(198, 40, 40, 0.1);
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #C62828;
                    margin-bottom: 16px;
                }
                
                .section-title {
                    font-size: 40px;
                    font-weight: 800;
                    margin-bottom: 16px;
                }
                
                .section-subtitle {
                    font-size: 18px;
                    color: #666;
                }
                
                .destination-card {
                    background: white;
                    border-radius: 16px;
                    padding: 30px 20px;
                    text-align: center;
                    transition: all 0.3s ease;
                    border-bottom: 3px solid;
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
                }
                
                .destination-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
                }
                
                .destination-flag {
                    margin-bottom: 16px;
                }
                
                .flag-image {
                    width: 60px;
                    height: 40px;
                    object-fit: cover;
                    border-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                
                .destination-name {
                    font-size: 18px;
                    font-weight: 700;
                    margin-bottom: 12px;
                }
                
                .destination-line {
                    width: 40px;
                    height: 3px;
                    margin: 0 auto;
                    border-radius: 3px;
                }
                
                /* Services Section */
                .services-section {
                    padding: 80px 0;
                    background: white;
                }
                
                .service-card {
                    border: none;
                    border-radius: 20px;
                    padding: 20px;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    overflow: hidden;
                    position: relative;
                    animation: fadeInUp 0.6s ease forwards;
                    opacity: 0;
                    height: 100%;
                }
                
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .service-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
                }
                
                .service-icon {
                    width: 80px;
                    height: 80px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 20px;
                    transition: transform 0.3s ease;
                }
                
                .service-card:hover .service-icon svg {
                    transform: scale(1.1);
                }
                
                .service-title {
                    font-size: 20px;
                    font-weight: 700;
                    margin-top: 20px;
                    margin-bottom: 12px;
                    text-align: center;
                }
                
                .service-desc {
                    text-align: center;
                    color: #666;
                    font-size: 14px;
                }
                
                .service-hover-line {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    transform: scaleX(0);
                    transition: transform 0.3s ease;
                }
                
                .service-card:hover .service-hover-line {
                    transform: scaleX(1);
                }
                
                /* About Section */
                .about-section {
                    padding: 80px 0;
                    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                }
                
                .about-image {
                    position: relative;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border-radius: 30px;
                    padding: 40px;
                    min-height: 400px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .about-logo {
                    max-width: 80%;
                    max-height: 280px;
                    object-fit: contain;
                    border-radius: 20px;
                    transition: transform 0.3s ease;
                }
                
                .about-logo:hover {
                    transform: scale(1.02);
                }
                
                .about-icon-fallback {
                    font-size: 80px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .about-image-badge {
                    position: absolute;
                    top: -20px;
                    right: -20px;
                    background: white;
                    border-radius: 20px;
                    padding: 12px 24px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
                }
                
                .about-image-badge span {
                    font-size: 18px;
                    font-weight: 800;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                
                .about-image-content {
                    text-align: center;
                    width: 100%;
                }
                
                .about-features {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 20px;
                    margin-top: 30px;
                }
                
                .about-feature {
                    display: flex;
                    gap: 12px;
                    padding: 12px;
                    background: #f8f9fa;
                    border-radius: 12px;
                    transition: all 0.3s ease;
                }
                
                .about-feature:hover {
                    transform: translateX(5px);
                    background: #fff;
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
                }
                
                .feature-icon {
                    font-size: 24px;
                }
                
                .feature-text strong {
                    display: block;
                    font-size: 16px;
                    margin-bottom: 4px;
                }
                
                .feature-text p {
                    font-size: 13px;
                    color: #666;
                    margin: 0;
                }
                
                /* CTA Section */
                .cta-section {
                    background: linear-gradient(135deg, #0a0a2a 0%, #1a1a4e 100%);
                    padding: 80px 0;
                }
                
                .cta-title {
                    font-size: 40px;
                    font-weight: 800;
                    color: white;
                    margin-bottom: 16px;
                }
                
                .cta-subtitle {
                    font-size: 18px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 32px;
                }
                
                .cta-buttons {
                    display: flex;
                    gap: 16px;
                    justify-content: center;
                    flex-wrap: wrap;
                }
                
                .btn-cta-primary {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    padding: 14px 32px;
                    border-radius: 50px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    color: white;
                }
                
                .btn-cta-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(198, 40, 40, 0.4);
                }
                
                .btn-cta-whatsapp {
                    background: #25D366;
                    border: none;
                    padding: 14px 32px;
                    border-radius: 50px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    color: white;
                }
                
                .btn-cta-whatsapp:hover {
                    transform: translateY(-2px);
                    background: #128C7E;
                }
                
                .other-numbers small {
                    font-size: 12px;
                }
                
                /* Skeleton Loader Styles */
                .skeleton-loader {
                    min-height: 100vh;
                    background: #f8f9fa;
                }
                
                .skeleton-hero {
                    height: 100vh;
                    background: linear-gradient(135deg, #1a1a3e 0%, #2a2a5e 100%);
                    animation: pulse 1.5s ease-in-out infinite;
                }
                
                .skeleton-section {
                    padding: 80px 0;
                }
                
                .skeleton-title {
                    width: 300px;
                    height: 40px;
                    background: #e0e0e0;
                    margin: 0 auto 30px;
                    border-radius: 8px;
                    animation: pulse 1.5s ease-in-out infinite;
                }
                
                .skeleton-cards {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 24px;
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 20px;
                }
                
                .skeleton-card {
                    height: 280px;
                    background: #e0e0e0;
                    border-radius: 20px;
                    animation: pulse 1.5s ease-in-out infinite;
                }
                
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                
                /* Animations */
                .fade-in-up {
                    animation: fadeInUp 0.8s ease forwards;
                }
                
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                /* Responsive */
                @media (max-width: 992px) {
                    .hero-title {
                        font-size: 40px;
                    }
                    
                    .section-title {
                        font-size: 32px;
                    }
                    
                    .about-features {
                        grid-template-columns: 1fr;
                    }
                    
                    .skeleton-cards {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
                
                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 32px;
                    }
                    
                    .hero-buttons {
                        flex-direction: column;
                    }
                    
                    .hero-buttons .btn {
                        width: 100%;
                    }
                    
                    .section-title {
                        font-size: 28px;
                    }
                    
                    .cta-title {
                        font-size: 28px;
                    }
                    
                    .flag-image {
                        width: 50px;
                        height: 35px;
                    }
                    
                    .about-logo {
                        max-width: 90%;
                    }
                    
                    .service-icon {
                        width: 60px;
                        height: 60px;
                    }
                    
                    .service-icon svg {
                        width: 35px;
                        height: 35px;
                    }
                    
                    .skeleton-cards {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
    </>
  );
};

export default Home;
